import React, { useEffect, useState } from "react";
import { ArrowLeft, Check, ChevronDown, FileText, Images, Loader2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BrandStudioShell from "@/components/brandStudio/BrandStudioShell";
import BrandStudioSignature from "@/components/brandStudio/BrandStudioSignature";
import PrivateReferenceImage from "@/components/masterOrder/PrivateReferenceImage";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { BRAND_STUDIO_ROUTES } from "@/lib/brandJourney.mjs";
import { approveVisualDirection, generateVisualDirectionBoard, generateVisualDirections, getVisualDirectionWorkspace, makeVisualDirectionBoardCurrent, refineVisualDirectionBoard, saveVisualDirectionFeedback, selectVisualDirection } from "@/lib/brandStudioApi";

const errorText = (error) => error?.response?.data?.detail || "Не удалось загрузить визуальное направление.";
const STATUS_LABELS = Object.freeze({ draft: "Черновик", proposed: "Предложено", selected: "Выбрано", approved: "Утверждено" });
const BOARD_DIRECTION_TITLES = new Set(["ÉDITORIAL ATELIER", "BOTANICAL SIGNATURE", "CONTEMPORARY SCHOOL"]);
const REFINEMENT_REVIEW_SECTIONS = Object.freeze([
  { key: "concept", label: "Концепция" },
  { key: "color_direction", label: "Цветовое направление", palette: true },
  { key: "typography_direction", label: "Типографика" },
  { key: "graphic_language", label: "Графический язык" },
  { key: "photography_direction", label: "Фотография" },
  { key: "material_direction", label: "Материалы и отделка" },
  { key: "packaging_direction", label: "Упаковка" },
  { key: "rationale", label: "Почему это подходит бренду" },
  { key: "avoid_direction", label: "Чего избегать" },
]);
const COLOR_DIRECTION_SWATCHES = Object.freeze([
  { key: "milk", label: "Молочный", color: "#F4EEE4", terms: ["молоч"] },
  { key: "cream", label: "Сливочный", color: "#EADCC4", terms: ["сливоч"] },
  { key: "almond", label: "Миндальный", color: "#D6B98A", terms: ["миндал"] },
  { key: "powder", label: "Пудрово-бежевый", color: "#D8C2B5", terms: ["пудров", "бежев"] },
  { key: "pistachio", label: "Фисташковый", color: "#A9AD7A", terms: ["фисташ"] },
  { key: "sage", label: "Шалфейный", color: "#87907A", terms: ["шалф"] },
  { key: "burgundy", label: "Бордовый акцент", color: "#7A2438", terms: ["бордов", "ягодно-винн"] },
  { key: "green", label: "Тёмно-зелёный", color: "#35483B", terms: ["тёмный зел", "темный зел"] },
  { key: "chocolate", label: "Глубокий шоколадный", color: "#4A3028", terms: ["шоколад"] },
]);

function SourceSummary({ state }) {
  return (
    <section className="mt-8 border-y border-[#E8DCCA] py-5" data-testid="visual-direction-source-summary">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex min-h-14 items-center gap-3">
          <Check size={17} className="shrink-0 text-[#7A6247]" />
          <div><div className="text-[11px] uppercase tracking-[0.14em] text-[#927A5B]">Основа бренда</div>
            <div className="mt-0.5 text-[13px] text-[#2A1D14]">Brand Brief готов</div></div>
        </div>
        <div className="flex min-h-14 items-center gap-3">
          <Images size={17} className="shrink-0 text-[#7A6247]" />
          <div><div className="text-[11px] uppercase tracking-[0.14em] text-[#927A5B]">Визуальные ориентиры</div>
            <div className="mt-0.5 text-[13px] text-[#2A1D14]">{state.reference_count} референсов</div></div>
        </div>
        <div className="flex min-h-14 items-center gap-3">
          <FileText size={17} className="shrink-0 text-[#7A6247]" />
          <div><div className="text-[11px] uppercase tracking-[0.14em] text-[#927A5B]">Комментарии</div>
            <div className="mt-0.5 text-[13px] text-[#2A1D14]">{state.reference_note_count} с описанием</div></div>
        </div>
      </div>
    </section>
  );
}

function DirectionSection({ label, children }) {
  if (!children) return null;
  return <div><div className="text-[10.5px] uppercase tracking-[0.15em] text-[#927A5B]">{label}</div>
    <div className="mt-1.5 text-[13.5px] leading-relaxed text-[#4C392B]">{children}</div></div>;
}

function PalettePreview({ content, versionNumber }) {
  const colorText = (content?.color_direction || "").toLocaleLowerCase("ru-RU");
  const colors = COLOR_DIRECTION_SWATCHES.filter((swatch) => swatch.terms.some((term) => colorText.includes(term)));
  if (!colors.length) return null;
  return <div className="mt-4 border-t border-[#EADFD0] pt-4" data-testid="visual-direction-version-palette">
    <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">Палитра Version {versionNumber}</div>
    <div className="mt-2.5 flex flex-wrap gap-3">{colors.map((swatch) =>
      <div key={swatch.key} className="flex min-w-[92px] items-center gap-2" data-color-key={swatch.key}>
        <span className={`h-8 w-8 shrink-0 rounded-full border shadow-sm ${swatch.key === "burgundy" ? "border-[#7A2438] ring-2 ring-[#7A2438]/20" : "border-black/10"}`}
              style={{ backgroundColor: swatch.color }} aria-hidden="true" />
        <span className="text-[10.5px] leading-tight text-[#5B4326]">{swatch.label}</span>
      </div>)}</div>
  </div>;
}

function RefinementReviewSection({ section, original, revised, revisionNumber }) {
  const originalValue = original?.[section.key] || "—";
  const revisedValue = revised?.[section.key] || "—";
  const changed = String(originalValue).trim() !== String(revisedValue).trim();
  return <article className="overflow-hidden rounded-2xl border border-[#E4D5C0] bg-[#FFFDF8]"
                  data-testid={`visual-direction-review-${section.key}`} data-changed={changed ? "true" : "false"}>
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E8DCCA] bg-[#F7F1E8] px-4 py-3 sm:px-5">
      <h3 className="text-[11px] uppercase tracking-[0.15em] text-[#5B4326]">{section.label}</h3>
      {changed ? <span className="rounded-full border border-[#8A3045]/25 bg-[#F5E5E8] px-2.5 py-1 text-[9.5px] uppercase tracking-[0.12em] text-[#7A2438]">Изменено</span>
        : <span className="text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">Без изменений</span>}
    </header>
    <div className="grid min-w-0 grid-cols-1 lg:grid-cols-2">
      <div className="min-w-0 bg-[#FAF7F1] px-4 py-4 sm:px-5 sm:py-5 lg:border-r lg:border-[#E8DCCA]">
        <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">Исходное направление</div>
        <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-[#5E4A3A]">{originalValue}</p>
      </div>
      <div className={`min-w-0 border-t px-4 py-4 sm:px-5 sm:py-5 lg:border-t-0 ${changed ? "border-[#D8B5BD] bg-[#FFFAF9]" : "border-[#E8DCCA] bg-[#FFFDF8]"}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[9.5px] uppercase tracking-[0.14em] text-[#7A6247]">Version {revisionNumber}</span>
          {changed && <span className="h-2 w-2 rounded-full bg-[#7A2438]" aria-hidden="true" />}
        </div>
        <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed text-[#3F2C22]">{revisedValue}</p>
        {section.palette && <PalettePreview content={revised} versionNumber={revisionNumber} />}
      </div>
    </div>
  </article>;
}

function RefinementReview({ direction, currentRevision }) {
  const revised = currentRevision.content_snapshot;
  return <section id="visual-direction-professional-analysis" className="mt-8 scroll-mt-6" data-testid="visual-direction-version-comparison">
    <div className="text-[10.5px] uppercase tracking-[0.15em] text-[#927A5B]">Сравнение версий</div>
    <div className="mt-3 rounded-2xl border border-[#D8C7AF] bg-[#F7F1E8] px-4 py-4 sm:px-5" data-testid="visual-direction-refinement-explanation">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-medium text-[#3F2C22]">Version {currentRevision.revision_number}</span>
        <span className="rounded-full bg-[#EDE2CF] px-2 py-1 text-[9.5px] uppercase tracking-[0.12em] text-[#5B4326]">Текущая</span>
      </div>
      <p className="mt-2 text-[12.5px] leading-relaxed text-[#6D5842]">Уточнена стратегия направления. Новая визуальная доска на этом этапе не создавалась.</p>
    </div>
    <div className="mt-4 grid gap-4">
      {REFINEMENT_REVIEW_SECTIONS.map((section) => <RefinementReviewSection key={section.key} section={section} original={direction} revised={revised} revisionNumber={currentRevision.revision_number} />)}
    </div>
  </section>;
}

function VisualBoardImage({ board, className = "h-full w-full object-contain" }) {
  return <PrivateReferenceImage
    reference={{ id: board.media_id, image_url: board.image_url }}
    className={className}
  />;
}

function VisualBoardComparison({ previous, current }) {
  const [mobileVersion, setMobileVersion] = useState("current");
  if (!previous || !current) return null;
  const mobileBoard = mobileVersion === "previous" ? previous : current;
  return <section className="mt-6 rounded-2xl border border-[#E4D5C0] bg-[#FFFDF8] p-4 sm:p-5"
      data-testid="visual-board-comparison">
    <h4 className="text-[20px] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Сравнить с предыдущей</h4>
    <div className="mt-4 hidden grid-cols-2 gap-4 md:grid" data-testid="visual-board-comparison-desktop">
      {[previous, current].map((item) => <div key={item.id} className="min-w-0">
        <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-[#806B52]">
          Visual Version {item.visual_version_number}{item.status === "current" ? " · текущая" : ""}
        </div>
        <div className="aspect-[2/3] overflow-hidden rounded-xl bg-[#F5F0E8]">
          <VisualBoardImage board={item} />
        </div>
      </div>)}
    </div>
    <div className="mt-4 md:hidden" data-testid="visual-board-comparison-mobile">
      <div className="grid grid-cols-2 rounded-xl bg-[#F1E8DB] p-1" role="group" aria-label="Выбор visual version">
        {[{ key: "previous", label: `Previous · V${previous.visual_version_number}` }, { key: "current", label: `Current · V${current.visual_version_number}` }].map((item) =>
          <button key={item.key} type="button" onClick={() => setMobileVersion(item.key)}
              aria-pressed={mobileVersion === item.key}
              className={`min-h-11 rounded-lg px-2 text-[11px] ${mobileVersion === item.key ? "bg-[#FFFDF8] text-[#3F2C22] shadow-sm" : "text-[#806B52]"}`}>
            {item.label}
          </button>)}
      </div>
      <div className="mt-3 aspect-[2/3] overflow-hidden rounded-xl bg-[#F5F0E8]">
        <VisualBoardImage board={mobileBoard} />
      </div>
    </div>
  </section>;
}

const visualFeedbackFor = (board, revisions) =>
  revisions.find((item) => item.id === board?.source_revision_id);

function PersistedDirection({ direction, references, boards, revisions, currentRevisionId, approval, owner, onGenerateBoard, generatingBoard, onSelect, selecting, onSaveFeedback, savingFeedback, onRefineBoard, refiningBoard, onMakeBoardCurrent, switchingBoard, onApprove, approving, showApprovalTransition, onCloseApprovalTransition, onGoVisualDNA, onBack }) {
  const evidence = references.filter((item) => direction.reference_ids.includes(item.id));
  const isSelected = direction.status === "selected";
  const directionRevisions = revisions
    .filter((item) => item.visual_direction_id === direction.id)
    .sort((left, right) => right.revision_number - left.revision_number);
  const currentRevision = directionRevisions.find((item) => item.id === currentRevisionId)
    || directionRevisions.find((item) => item.status === "current");
  const hasRefinedRevision = directionRevisions.some((item) => Boolean(item.ai_job_id));
  const directionBoards = [...(boards || [])].sort((left, right) => right.visual_version_number - left.visual_version_number);
  const currentBoard = directionBoards.find((item) => item.status === "current") || directionBoards[0];
  const previousBoard = currentBoard?.source_board_id
    ? directionBoards.find((item) => item.id === currentBoard.source_board_id)
    : directionBoards.find((item) => item.visual_version_number < (currentBoard?.visual_version_number || 0))
      || directionBoards.find((item) => item.id !== currentBoard?.id);
  const [viewedBoardId, setViewedBoardId] = useState("");
  const viewedBoard = directionBoards.find((item) => item.id === viewedBoardId);
  const displayedBoard = viewedBoard || currentBoard;
  const displayedFeedback = visualFeedbackFor(displayedBoard, directionRevisions);
  const displayedBoardApproved = approval?.visual_board_id === displayedBoard?.id;
  const currentBoardApproved = approval?.visual_board_id === currentBoard?.id;
  const currentRevisionAlreadyRendered = directionBoards.some((item) => item.source_revision_id === currentRevision?.id);
  const [feedback, setFeedback] = useState("");
  const [preserveFeedback, setPreserveFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [confirmingRefinement, setConfirmingRefinement] = useState(false);
  const [confirmingApproval, setConfirmingApproval] = useState(false);
  const [refinementError, setRefinementError] = useState("");
  const [approvalError, setApprovalError] = useState("");
  useEffect(() => {
    setFeedback(""); setPreserveFeedback(""); setFeedbackError("");
    setConfirmingRefinement(false); setConfirmingApproval(false);
    setRefinementError(""); setApprovalError(""); setViewedBoardId("");
  }, [direction.id, currentRevisionId]);
  const continueWithDirection = () => {
    document.getElementById("visual-direction-refinement-foundation")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const submitFeedback = async (event) => {
    event.preventDefault();
    const value = feedback.trim();
    if (!value || savingFeedback) return;
    setFeedbackError("");
    try { await onSaveFeedback(direction.id, value, preserveFeedback.trim()); }
    catch (requestError) { setFeedbackError(errorText(requestError)); }
  };
  const submitRefinement = async () => {
    if (refiningBoard) return;
    setRefinementError("");
    try { await onRefineBoard(direction.id); setConfirmingRefinement(false); }
    catch (requestError) { setRefinementError(errorText(requestError)); setConfirmingRefinement(false); }
  };
  const submitApproval = async () => {
    if (approving || !currentBoard) return;
    setApprovalError("");
    try { await onApprove(direction.id); setConfirmingApproval(false); }
    catch (requestError) { setApprovalError(errorText(requestError)); setConfirmingApproval(false); }
  };
  if (showApprovalTransition) return <section className="mx-auto max-w-3xl py-10 sm:py-16" data-testid="visual-direction-approved-transition">
    <div className="rounded-3xl border border-[#D9C8AE] bg-[#FFFDF8] px-5 py-9 text-center sm:px-10 sm:py-12">
      <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[#E7E4D6] text-[#53604A]"><Check size={22} /></span>
      <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-[#8B6F4B]">Утверждённое направление</div>
      <h1 className="mt-2 text-[38px] leading-none text-[#2A1D14] sm:text-[48px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Направление найдено</h1>
      <p className="mx-auto mt-4 max-w-xl text-[14px] leading-relaxed text-[#5B4326]">Теперь соберём из него рабочую визуальную систему бренда.</p>
      <div className="mt-7 flex flex-col justify-center gap-2 sm:flex-row">
        <Button type="button" onClick={onGoVisualDNA} className="min-h-11 bg-[#4A3728] text-[#F7EFE0]">Открыть мой бренд</Button>
        <Button type="button" variant="outline" onClick={onCloseApprovalTransition} className="min-h-11 border-[#D9C39F] bg-white text-[#4A3728]">Посмотреть утверждённое направление</Button>
      </div>
    </div>
  </section>;
  return (
    <article className="min-w-0" data-testid="visual-direction-detail">
      <button type="button" onClick={onBack}
              className="mb-7 inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-[#D9C39F] bg-[#FFFDF8] px-3 text-[12.5px] text-[#4A3728] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50"
              data-testid="visual-direction-detail-back">
        <ArrowLeft size={14} /> К сравнению направлений
      </button>
      <div className="border-t border-[#E6D8C3] pt-8" data-testid="visual-direction-persisted">
      {displayedBoard?.image_url && <section className="mb-9 flex w-full flex-col items-center" data-testid="visual-direction-board"
        data-visual-version={displayedBoard.visual_version_number} data-board-status={displayedBoard.status}
        data-image-input-units={displayedBoard.image_input_units} data-image-output-units={displayedBoard.image_output_units}
        data-text-input-units={displayedBoard.text_input_units} data-actual-cost-micros={displayedBoard.actual_cost_micros}>
        <div className="aspect-[2/3] w-full max-w-[520px] overflow-hidden rounded-2xl bg-[#F5F0E8] shadow-[0_18px_50px_rgba(62,42,27,0.10)] md:h-[760px] md:w-auto md:max-w-full">
          <VisualBoardImage board={displayedBoard} />
        </div>
        <div className="mt-4 w-full max-w-[520px] rounded-xl border border-[#E4D5C0] bg-[#FFFDF8] px-4 py-3" data-testid="visual-board-provenance">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-[#5B4326]">Visual Version {displayedBoard.visual_version_number}</span>
            <span className="text-[9.5px] uppercase tracking-[0.12em] text-[#806B52]">{displayedBoard.status === "current" ? "Текущая" : "История"}</span>
          </div>
          {displayedBoard.visual_version_number === 1
            ? <p className="mt-1.5 text-[11.5px] text-[#806B52]">Исходная концептуальная доска</p>
            : <><p className="mt-1.5 line-clamp-2 text-[11.5px] leading-relaxed text-[#6D5842]">{displayedFeedback?.user_feedback}</p>
              <p className="mt-1 text-[10.5px] text-[#927A5B]">OpenAI · {displayedBoard.model} · ${(displayedBoard.actual_cost_micros / 1_000_000).toFixed(6)} USD</p></>}
          {displayedBoardApproved && <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="visual-direction-approved-badge">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E7E4D6] px-2.5 py-1 text-[9.5px] uppercase tracking-[0.11em] text-[#53604A]"><Check size={11} /> Утверждённое направление</span>
            <Button type="button" variant="outline" onClick={onGoVisualDNA} className="min-h-10 border-[#D9C39F] bg-white px-3 text-[#4A3728]">Открыть мой бренд</Button>
          </div>}
          {owner && displayedBoard?.status === "current" && !currentBoardApproved && <div className="mt-4" data-testid="visual-direction-approval-action">
            {!confirmingApproval ? <Button type="button" onClick={() => setConfirmingApproval(true)}
                className="min-h-11 bg-[#7A2438] px-4 text-white" data-testid="visual-direction-approval-open">
              Это моё направление
            </Button> : <div className="rounded-xl border border-[#D8B5BD] bg-[#FFFAF9] p-4" role="alertdialog" aria-label="Утверждение визуального направления"
                data-testid="visual-direction-approval-confirmation">
              <h3 className="text-[18px] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Утвердить это направление?</h3>
              <p className="mt-2 text-[12.5px] leading-relaxed text-[#5B4326]">Эта версия станет основой визуальной ДНК бренда. Исходные направления, предыдущие версии и история уточнений сохранятся.</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button type="button" onClick={submitApproval} disabled={approving} className="min-h-11 bg-[#7A2438] px-4 text-white disabled:opacity-60" data-testid="visual-direction-approval-confirm">
                  {approving ? "Утверждаем…" : "Да, это моё направление"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setConfirmingApproval(false)} disabled={approving} className="min-h-11 border-[#D9C39F] bg-white text-[#4A3728]">Вернуться</Button>
              </div>
            </div>}
            {approvalError && <p className="mt-3 text-[12px] text-[#8B3F35]" role="alert">{approvalError}</p>}
          </div>}
          {viewedBoard && viewedBoard.status !== "current" && owner && <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" onClick={() => onMakeBoardCurrent(direction.id, viewedBoard.id)} disabled={switchingBoard}
                className="min-h-10 bg-[#4A3728] px-4 text-[#F7EFE0] disabled:opacity-60" data-testid="visual-board-make-current">
              {switchingBoard ? "Переключаем…" : "Сделать текущей"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setViewedBoardId("")}
                className="min-h-10 border-[#D9C39F] bg-white text-[#4A3728]">Вернуться к текущей</Button>
          </div>}
        </div>
      </section>}
      {directionBoards.length > 0 && <section className="mb-8" data-testid="visual-board-history">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[10.5px] uppercase tracking-[0.15em] text-[#927A5B]">Visual history</h3>
          <span className="text-[10.5px] text-[#927A5B]">{directionBoards.length} {directionBoards.length === 1 ? "версия" : "версии"}</span>
        </div>
        <div className="mt-3 flex gap-3 overflow-x-auto pb-2">{directionBoards.map((item) => {
          const itemFeedback = visualFeedbackFor(item, directionRevisions);
          return <button key={item.id} type="button" onClick={() => setViewedBoardId(item.status === "current" ? "" : item.id)}
              className={`w-[112px] shrink-0 rounded-xl border p-2 text-left ${displayedBoard?.id === item.id ? "border-[#8B6F4B] bg-[#FFFDF8]" : "border-[#E4D5C0] bg-[#FAF7F1]"}`}
              data-testid={`visual-board-history-${item.visual_version_number}`}>
            <span className="block aspect-[2/3] overflow-hidden rounded-lg bg-[#F1E8DB]"><VisualBoardImage board={item} /></span>
            <span className="mt-2 block text-[10.5px] font-medium text-[#4A3728]">Version {item.visual_version_number}</span>
            <time className="mt-0.5 block text-[9px] text-[#927A5B]" dateTime={item.created_at}>{new Date(item.created_at).toLocaleDateString("ru-RU")}</time>
            {itemFeedback && <span className="mt-1 block truncate text-[9.5px] text-[#806B52]">{itemFeedback.user_feedback}</span>}
            {item.status === "current" && <span className="mt-1 block text-[9px] uppercase tracking-[0.1em] text-[#7A2438]">Текущая</span>}
          </button>})}</div>
        {directionBoards.length > 1 && <VisualBoardComparison previous={previousBoard} current={currentBoard} />}
      </section>}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><div className="text-[10.5px] uppercase tracking-[0.18em] text-[#927A5B]">Визуальная концепция</div>
          <h2 className="mt-2 text-[30px] leading-tight text-[#2A1D14] md:text-[38px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{direction.title}</h2></div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] ${isSelected ? "border-[#C7A574] bg-[#EDE2CF] text-[#5B4326]" : "border-[#E1D2BB] text-[#806B52]"}`}
              data-testid={isSelected ? "visual-direction-detail-selected-badge" : undefined}>
          {isSelected && <Check size={12} />} {isSelected ? "Направление выбрано" : (STATUS_LABELS[direction.status] || direction.status)}
        </span>
      </div>
      <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[#4C392B]">{direction.concept}</p>
      <div className="mt-5 flex flex-wrap gap-2">{direction.keywords.map((keyword) =>
        <span key={keyword} className="rounded-full bg-[#F5EFE5] px-2.5 py-1 text-[11px] text-[#6D5842]">{keyword}</span>)}</div>
      {owner && isSelected && approval ? <div className="mt-6 flex flex-col gap-2 sm:flex-row" data-testid="visual-direction-approved-actions">
        <Button type="button" onClick={onGoVisualDNA}
                className="min-h-11 bg-[#4A3728] text-[#F7EFE0]"
                data-testid="visual-direction-open-brand">
          Открыть мой бренд
        </Button>
      </div> : owner && isSelected ? <div className="mt-6 flex flex-col gap-2 sm:flex-row" data-testid="visual-direction-selected-actions">
        <Button type="button" onClick={continueWithDirection}
                className="min-h-11 bg-[#4A3728] text-[#F7EFE0]"
                data-testid="visual-direction-continue-selected">
          Продолжить с этим направлением
        </Button>
        <Button type="button" variant="outline" onClick={onBack}
                className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728]"
                data-testid="visual-direction-choose-another">
          Выбрать другое направление
        </Button>
      </div> : owner && <div className="mt-6" data-testid="visual-direction-selection-action">
        <Button type="button" onClick={() => onSelect(direction.id)}
                disabled={selecting}
                className="min-h-11 bg-[#4A3728] text-[#F7EFE0] disabled:opacity-70">
          {selecting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Сохраняем выбор…</>
            : "Выбрать это направление"}
        </Button>
      </div>}
      {owner && isSelected && <section id="visual-direction-refinement-foundation" className="mt-8 max-w-3xl scroll-mt-6 rounded-2xl border border-[#E6D8C3] bg-[#FFFDF8] p-5 sm:p-6"
          data-testid="visual-direction-refinement-foundation">
        <div className="text-[10.5px] uppercase tracking-[0.16em] text-[#927A5B]">Следующий этап</div>
        <h3 className="mt-1.5 text-[25px] leading-tight text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Уточнить направление</h3>
        <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-[#6D5842]">Расскажи обычными словами, что изменить в текущей доске и что обязательно оставить узнаваемым.</p>
        {currentRevision && <div className="mt-4 rounded-xl bg-[#F5EFE5] px-4 py-3" data-testid="visual-direction-current-feedback">
          <div className="text-[10px] uppercase tracking-[0.13em] text-[#927A5B]">Сохранено · версия {currentRevision.revision_number}</div>
          <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-[#4C392B]">{currentRevision.user_feedback}</p>
          {currentRevision.preserve_feedback && <p className="mt-2 whitespace-pre-wrap border-t border-[#E4D5C0] pt-2 text-[12px] leading-relaxed text-[#6D5842]"><span className="font-medium">Сохранить:</span> {currentRevision.preserve_feedback}</p>}
        </div>}
        {currentBoard && currentRevision && !currentRevisionAlreadyRendered && <div className="mt-4" data-testid="visual-direction-refinement-action">
          {!confirmingRefinement ? <Button type="button" onClick={() => setConfirmingRefinement(true)}
              className="min-h-10 bg-[#4A3728] px-4 text-[#F7EFE0]" data-testid="visual-direction-refine-open-confirmation">
            Создать новую visual version
          </Button> : <div className="rounded-xl border border-[#D9C39F] bg-white p-4" role="alertdialog" aria-label="Подтверждение платного уточнения"
              data-testid="visual-direction-refinement-confirmation">
            <p className="text-[12.5px] leading-relaxed text-[#4C392B]">Будет создана одна новая платная visual version на основе текущей доски и сохранённых пожеланий. Автоматический повтор отключён.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" onClick={submitRefinement} disabled={refiningBoard}
                      className="min-h-10 bg-[#4A3728] px-4 text-[#F7EFE0] disabled:opacity-60"
                      data-testid="visual-direction-refine-confirm">
                {refiningBoard ? <><Loader2 size={15} className="mr-2 animate-spin" /> Создаём visual version…</> : "Подтвердить и создать"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setConfirmingRefinement(false)} disabled={refiningBoard}
                      className="min-h-10 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728]">Отмена</Button>
            </div>
          </div>}
          {refinementError && <p className="mt-3 text-[12.5px] text-[#8B3F35]" role="alert" data-testid="visual-direction-refinement-error">{refinementError}</p>}
        </div>}
        {currentRevisionAlreadyRendered && <p className="mt-4 text-[11.5px] text-[#806B52]" data-testid="visual-board-feedback-used">Для этих пожеланий visual version уже создана. Сохрани новые пожелания для следующей версии.</p>}
        <form className="mt-4" onSubmit={submitFeedback}>
          <label htmlFor={`direction-feedback-${direction.id}`} className="text-[12px] font-medium text-[#4A3728]">Что хочется изменить?</label>
          <textarea id={`direction-feedback-${direction.id}`} value={feedback} onChange={(event) => setFeedback(event.target.value)}
                    maxLength={5000} rows={4} placeholder="Например: сделать теплее, уменьшить строгость и сохранить редакционную типографику"
                    className="mt-2 w-full resize-y rounded-xl border border-[#D9C39F] bg-white px-3.5 py-3 text-[13px] leading-relaxed text-[#2A1D14] outline-none placeholder:text-[#A28D73] focus:border-[#9B7A52] focus:ring-2 focus:ring-[#C7A574]/25"
                    data-testid="visual-direction-feedback-input" />
          <label htmlFor={`direction-preserve-${direction.id}`} className="mt-4 block text-[12px] font-medium text-[#4A3728]">Что обязательно сохранить?</label>
          <textarea id={`direction-preserve-${direction.id}`} value={preserveFeedback} onChange={(event) => setPreserveFeedback(event.target.value)}
                    maxLength={5000} rows={3} placeholder="Например: воздушную композицию, молочный фон и редакционную типографику"
                    className="mt-2 w-full resize-y rounded-xl border border-[#D9C39F] bg-white px-3.5 py-3 text-[13px] leading-relaxed text-[#2A1D14] outline-none placeholder:text-[#A28D73] focus:border-[#9B7A52] focus:ring-2 focus:ring-[#C7A574]/25"
                    data-testid="visual-direction-preserve-input" />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={!feedback.trim() || savingFeedback}
                    className="min-h-10 bg-[#4A3728] px-4 text-[#F7EFE0] disabled:opacity-60"
                    data-testid="visual-direction-feedback-save">
              {savingFeedback ? <><Loader2 size={15} className="mr-2 animate-spin" /> Сохраняем…</> : "Сохранить пожелания"}
            </Button>
            <span className="text-[11px] text-[#927A5B]">Пожелания сохраняются в существующую историю стратегии.</span>
          </div>
          {feedbackError && <p className="mt-3 text-[12.5px] text-[#8B3F35]" role="alert">{feedbackError}</p>}
        </form>
        {directionRevisions.length > 0 && <details className="mt-6 border-t border-[#E8DCCA] pt-5" data-testid="visual-direction-revision-history">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between text-[10.5px] uppercase tracking-[0.15em] text-[#927A5B]">История стратегии <ChevronDown size={15} /></summary>
          <ol className="mt-3 space-y-3">{directionRevisions.map((revision) =>
            <li key={revision.id} className="rounded-xl border border-[#E8DCCA] px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-[10.5px] text-[#927A5B]">
                <span>Версия {revision.revision_number} · {revision.status === "current" ? "текущая" : "предыдущая"}</span>
                <time dateTime={revision.created_at}>{new Date(revision.created_at).toLocaleString("ru-RU")}</time>
              </div>
              <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-[#5B4326]">{revision.user_feedback}</p>
              {revision.preserve_feedback && <p className="mt-1 text-[11.5px] text-[#806B52]">Сохранить: {revision.preserve_feedback}</p>}
              {revision.ai_job_id && <p className="mt-2 text-[10.5px] text-[#927A5B]">AI refinement · {revision.model} · ${(revision.actual_cost_micros / 1_000_000).toFixed(6)}</p>}
            </li>)}</ol>
        </details>}
      </section>}
      {owner && BOARD_DIRECTION_TITLES.has(direction.title) && !currentBoard && <div className="mt-6" data-testid="visual-direction-board-owner-experiment">
        <Button type="button" onClick={() => onGenerateBoard(direction.id)} disabled={generatingBoard}
                className="min-h-11 bg-[#4A3728] text-[#F7EFE0] disabled:opacity-60">
          {generatingBoard ? <><Loader2 size={16} className="mr-2 animate-spin" /> Создаём concept board…</>
            : <><Sparkles size={16} className="mr-2" /> Создать concept board</>}
        </Button>
      </div>}
      <details className="group mt-8 rounded-2xl border border-[#E4D5C0] bg-[#FFFDF8] p-4 sm:p-5" data-testid="visual-direction-professional-detail">
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between text-[13px] font-medium text-[#4A3728]">Что изменилось <ChevronDown size={16} className="transition-transform group-open:rotate-180" /></summary>
      {hasRefinedRevision && currentRevision ? <RefinementReview direction={direction} currentRevision={currentRevision} />
        : <div id="visual-direction-professional-analysis" className="mt-5 grid scroll-mt-6 gap-x-8 gap-y-6 md:grid-cols-2">
        <DirectionSection label="Цветовое направление">{direction.color_direction}</DirectionSection>
        <DirectionSection label="Типографика">{direction.typography_direction}</DirectionSection>
        <DirectionSection label="Графический язык">{direction.graphic_language}</DirectionSection>
        <DirectionSection label="Фотография">{direction.photography_direction}</DirectionSection>
        <DirectionSection label="Материалы и отделка">{direction.material_direction}</DirectionSection>
        <DirectionSection label="Упаковка">{direction.packaging_direction}</DirectionSection>
        <DirectionSection label="Почему это подходит бренду">{direction.rationale}</DirectionSection>
        <DirectionSection label="Чего избегать">{direction.avoid_direction}</DirectionSection>
      </div>}
      </details>
      {evidence.length > 0 && <section className="mt-7" data-testid="visual-direction-evidence">
        <div className="text-[10.5px] uppercase tracking-[0.15em] text-[#927A5B]">Опора на твои референсы</div>
        <div className="mt-3 grid max-w-2xl grid-cols-3 gap-2 sm:grid-cols-4">
          {evidence.map((reference) => <div key={reference.id} className="aspect-[4/3] overflow-hidden rounded-xl bg-[#F5F0E8]">
            <PrivateReferenceImage reference={reference} className="h-full w-full object-contain" />
          </div>)}
        </div>
      </section>}
      </div>
    </article>
  );
}

function DirectionComparisonCard({ direction, board, approved, onOpen, onOpenBrand, onChangeSelection }) {
  const isSelected = direction.status === "selected";
  return (
    <article className={`flex min-w-0 flex-col overflow-hidden rounded-2xl border bg-[#FFFDF8] ${isSelected ? "border-[#8B6F4B] ring-2 ring-[#C7A574]/35" : "border-[#E6D8C3]"}`}
             data-selected={isSelected ? "true" : "false"}
             data-testid={`visual-direction-comparison-card-${direction.id}`}>
      <div className="flex min-h-[330px] items-center justify-center bg-[#F5F0E8] p-3 sm:min-h-[390px] lg:min-h-0 lg:aspect-[2/3]"
           data-testid="visual-direction-comparison-board">
        {board && board.image_url ?
          <PrivateReferenceImage reference={{ id: board.media_id, image_url: board.image_url }}
                                 className="h-full w-full max-w-[260px] object-contain lg:max-w-full" />
          : <div className="flex aspect-[2/3] w-full max-w-[220px] items-center justify-center rounded-xl border border-dashed border-[#D8C7AF] px-5 text-center text-[11.5px] leading-relaxed text-[#927A5B]">
              Concept board пока не создана
            </div>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        {isSelected && <div className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#EDE2CF] px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-[#5B4326]"
                            data-testid="visual-direction-selected-badge"><Check size={12} /> {approved ? "Утверждено" : "Выбрано"}</div>}
        <h2 className="text-[25px] leading-[1.05] text-[#2A1D14]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{direction.title}</h2>
        <p className="mt-3 text-[13px] leading-relaxed text-[#5B4326]"
           style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
           data-testid="visual-direction-comparison-concept">{direction.concept}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">{direction.keywords.map((keyword) =>
          <span key={keyword} className="rounded-full bg-[#F5EFE5] px-2 py-1 text-[10.5px] text-[#6D5842]">{keyword}</span>)}</div>
        {isSelected ? <div className="mt-5 flex min-h-11 items-center gap-3" data-testid="visual-direction-selected-card-actions">
          <Button type="button" onClick={() => approved ? onOpenBrand() : onOpen(direction.id)}
                  className="h-11 min-w-0 flex-1 whitespace-normal bg-[#4A3728] px-3 text-[11.5px] leading-tight text-[#F7EFE0]"
                  data-testid={`visual-direction-continue-${direction.id}`}>
            {approved ? "Открыть мой бренд" : "Продолжить с этим направлением"}
          </Button>
          <button type="button" onClick={() => onChangeSelection(direction.id)}
                  className="shrink-0 whitespace-nowrap px-1 py-1 text-[11.5px] leading-tight text-[#6D5842] underline decoration-[#BDA47E] underline-offset-4 focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50"
                  data-testid={`visual-direction-change-${direction.id}`}>
            Изменить выбор
          </button>
        </div> : <Button type="button" onClick={() => onOpen(direction.id)}
                    className="mt-5 min-h-11 w-full bg-[#4A3728] text-[#F7EFE0]"
                    data-testid={`visual-direction-open-${direction.id}`}>
          Рассмотреть направление
        </Button>}
      </div>
    </article>
  );
}

export default function MOVisualDirection() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [retryRequired, setRetryRequired] = useState(false);
  const [generatingBoard, setGeneratingBoard] = useState("");
  const [selectingDirection, setSelectingDirection] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [refiningBoard, setRefiningBoard] = useState("");
  const [switchingBoard, setSwitchingBoard] = useState("");
  const [approvingDirection, setApprovingDirection] = useState("");
  const [showApprovalTransition, setShowApprovalTransition] = useState(false);
  const [selectedDirectionId, setSelectedDirectionId] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try { setState(await getVisualDirectionWorkspace()); }
    catch (requestError) { setError(errorText(requestError)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (generating || user?.role !== "owner") return;
    setGenerating(true); setGenerationError("");
    try {
      setState(await generateVisualDirections({ retry: retryRequired }));
      setRetryRequired(false);
    } catch (requestError) {
      setGenerationError(errorText(requestError));
      setRetryRequired(true);
    } finally { setGenerating(false); }
  };

  const generateBoard = async (directionId) => {
    if (generatingBoard || user?.role !== "owner") return;
    setGeneratingBoard(directionId); setGenerationError("");
    try {
      setState(await generateVisualDirectionBoard(directionId));
    }
    catch (requestError) { setGenerationError(errorText(requestError)); }
    finally { setGeneratingBoard(""); }
  };

  const selectDirection = async (directionId) => {
    if (selectingDirection || user?.role !== "owner") return;
    setSelectingDirection(directionId); setGenerationError("");
    try { setState(await selectVisualDirection(directionId)); }
    catch (requestError) { setGenerationError(errorText(requestError)); }
    finally { setSelectingDirection(""); }
  };

  const saveFeedback = async (directionId, feedback, preserveFeedback) => {
    if (savingFeedback || user?.role !== "owner") return;
    setSavingFeedback(true);
    try { setState(await saveVisualDirectionFeedback(directionId, feedback, preserveFeedback)); }
    finally { setSavingFeedback(false); }
  };

  const refineBoard = async (directionId) => {
    if (refiningBoard || user?.role !== "owner") return;
    setRefiningBoard(directionId);
    try { setState(await refineVisualDirectionBoard(directionId)); }
    finally { setRefiningBoard(""); }
  };

  const makeBoardCurrent = async (directionId, boardId) => {
    if (switchingBoard || user?.role !== "owner") return;
    setSwitchingBoard(boardId);
    try { setState(await makeVisualDirectionBoardCurrent(directionId, boardId)); }
    finally { setSwitchingBoard(""); }
  };

  const approveDirection = async (directionId) => {
    if (approvingDirection || user?.role !== "owner") return;
    setApprovingDirection(directionId);
    try {
      setState(await approveVisualDirection(directionId));
      setShowApprovalTransition(true);
    } finally { setApprovingDirection(""); }
  };

  const selectedDirection = state?.state === "directions"
    ? state.directions.find((direction) => direction.id === selectedDirectionId)
    : null;
  const boardsFor = (directionId) => state?.boards?.filter((item) => item.visual_direction_id === directionId) || [];
  const boardFor = (directionId) => boardsFor(directionId).find((item) => item.status === "current")
    || boardsFor(directionId).sort((left, right) => right.visual_version_number - left.visual_version_number)[0];
  const changeSelection = (directionId) => {
    const alternative = state?.directions?.find((direction) => direction.id !== directionId);
    if (!alternative) return;
    const action = document.querySelector(`[data-testid="visual-direction-open-${alternative.id}"]`);
    action?.scrollIntoView({ behavior: "smooth", block: "center" });
    action?.focus({ preventScroll: true });
  };

  return (
    <BrandStudioShell title="Визуальное направление">
      <main className="w-full max-w-5xl min-w-0 overflow-x-clip pb-16" data-testid="mo-visual-direction">
        <button type="button" onClick={() => nav(BRAND_STUDIO_ROUTES.home)}
                className="mb-7 min-h-11 rounded-xl bg-[#F7F1E7] px-3 text-[12.5px] text-[#4A3728] inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C7A574]/50">
          <ArrowLeft size={14} /> Вернуться в Brand Studio
        </button>
        {loading ? <div className="min-h-[280px] grid place-items-center text-[13px] text-[#806B52]" data-testid="visual-direction-loading">
          <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Загружаем основу…</span></div>
        : error ? <section className="py-10"><h1 className="text-3xl font-serif text-[#2A1D14]">Не удалось загрузить основу</h1>
          <p className="mt-3 text-sm text-[#806B52]">{error}</p><Button onClick={load} className="mt-5">Повторить</Button></section>
        : state.state === "needs_brand_brief" ? <section className="max-w-2xl py-10" data-testid="visual-direction-needs-brand-brief">
          <div className="text-[10.5px] tracking-[0.2em] uppercase text-[#8B6F4B]">ВИЗУАЛЬНОЕ НАПРАВЛЕНИЕ</div>
          <h1 className="mt-2 text-[38px] leading-tight font-serif text-[#2A1D14]">Сначала расскажи о своём бренде</h1>
          <p className="mt-4 text-[14px] leading-relaxed text-[#5B4326]">Brand Brief станет основой будущего визуального направления.</p>
          <Button onClick={() => nav(BRAND_STUDIO_ROUTES.create)} className="mt-6 min-h-11 bg-[#4A3728] text-[#F7EFE0]">Начать создание</Button>
        </section>
        : state.state === "needs_references" ? <section className="max-w-2xl py-10" data-testid="visual-direction-needs-references">
          <div className="text-[10.5px] tracking-[0.2em] uppercase text-[#8B6F4B]">ВИЗУАЛЬНОЕ НАПРАВЛЕНИЕ</div>
          <h1 className="mt-2 text-[38px] leading-tight font-serif text-[#2A1D14]">Добавь визуальные ориентиры</h1>
          <p className="mt-4 text-[14px] leading-relaxed text-[#5B4326]">Достаточно одного содержательного референса. Описание можно добавить, но оно не обязательно.</p>
          <Button onClick={() => nav(BRAND_STUDIO_ROUTES.references)} className="mt-6 min-h-11 bg-[#4A3728] text-[#F7EFE0]">Добавить референсы</Button>
        </section>
        : selectedDirection ? <PersistedDirection direction={selectedDirection} references={state.references}
            boards={boardsFor(selectedDirection.id)} revisions={state.revisions || []}
            currentRevisionId={state.current_revision_id} approval={state.approval} owner={user?.role === "owner"}
            onGenerateBoard={generateBoard} generatingBoard={generatingBoard === selectedDirection.id}
            onSelect={selectDirection} selecting={selectingDirection === selectedDirection.id}
            onSaveFeedback={saveFeedback} savingFeedback={savingFeedback}
            onRefineBoard={refineBoard} refiningBoard={refiningBoard === selectedDirection.id}
            onMakeBoardCurrent={makeBoardCurrent} switchingBoard={Boolean(switchingBoard)}
            onApprove={approveDirection} approving={approvingDirection === selectedDirection.id}
            showApprovalTransition={showApprovalTransition}
            onCloseApprovalTransition={() => setShowApprovalTransition(false)}
            onGoVisualDNA={() => nav(BRAND_STUDIO_ROUTES.visualDNA)}
            onBack={() => setSelectedDirectionId("")} />
        : <>
          <header className="max-w-3xl" data-testid="visual-direction-ready">
            <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#8B6F4B]">ВИЗУАЛЬНОЕ НАПРАВЛЕНИЕ</div>
            <h1 className="mt-2 text-[38px] leading-[1.02] text-[#2A1D14] md:text-[50px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Найдём характер твоего бренда</h1>
            <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-[#5B4326]">Основа уже собрана: твой рассказ о бренде и визуальные ориентиры. Следующий этап превратит их в три профессиональных направления.</p>
            <div className="mt-4 max-w-2xl rounded-xl border border-[#E4D5C0] bg-[#FFFDF8] px-4 py-3" data-testid="visual-direction-product-education">
              <p className="text-[12.5px] leading-relaxed text-[#5B4326]">Три направления — не три финальных дизайна. Это три точки входа в поиск твоего визуального языка.</p>
              <p className="mt-1 text-[11.5px] leading-relaxed text-[#806B52]">Выбери то, которое ближе по ощущению. Дальше его можно уточнять: усиливать цвета, менять настроение, добавлять характер, убирать то, что не нравится, и сохранять то, что важно.</p>
            </div>
          </header>
          <SourceSummary state={state} />
          <div className="mt-5 flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => nav(BRAND_STUDIO_ROUTES.create)} className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728]">Изменить основу бренда</Button>
            <Button variant="outline" onClick={() => nav(BRAND_STUDIO_ROUTES.references)} className="min-h-11 border-[#D9C39F] bg-[#FFFDF8] text-[#4A3728]">Посмотреть референсы</Button>
          </div>
          {state.state === "directions" ? <section className="mt-10 min-w-0" data-testid="visual-direction-results">
            <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-3" data-testid="visual-direction-comparison">
              {state.directions.map((direction) => <DirectionComparisonCard key={direction.id} direction={direction}
                board={boardFor(direction.id)} approved={state.approval?.visual_direction_id === direction.id}
                onOpen={setSelectedDirectionId} onOpenBrand={() => nav(BRAND_STUDIO_ROUTES.visualDNA)} onChangeSelection={changeSelection} />)}
            </div>
            {generationError && <div className="mt-4 max-w-xl rounded-xl border border-[#E3D4BE] bg-[#FFFDF8] p-4" role="alert" data-testid="visual-direction-generation-error">
              <p className="text-[13px] leading-relaxed text-[#5B4326]">{generationError}</p>
            </div>}
          </section> : <section className="mt-10 max-w-3xl border-t border-[#E8DCCA] pt-8" data-testid="visual-direction-foundation-state">
            <div className="text-[10.5px] tracking-[0.18em] uppercase text-[#8B6F4B]">ОСНОВА ГОТОВА</div>
            <h2 className="mt-2 text-[28px] leading-tight font-serif text-[#2A1D14]">Всё необходимое для следующего этапа собрано</h2>
            <p className="mt-3 text-[13.5px] leading-relaxed text-[#5B4326]">Brand Studio проанализирует основу бренда и реальные визуальные ориентиры, чтобы создать три профессиональных направления.</p>
            {user?.role === "owner" ? <div className="mt-6" data-testid="visual-direction-owner-experiment">
              <Button type="button" onClick={generate} disabled={generating}
                      className="min-h-11 bg-[#4A3728] text-[#F7EFE0] disabled:opacity-60">
                {generating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Создаём направления…</>
                  : <><Sparkles size={16} className="mr-2" /> {retryRequired ? "Повторить создание" : "Найти визуальное направление"}</>}
              </Button>
              {generating && <p className="mt-3 text-[12.5px] text-[#806B52]" aria-live="polite">Анализируем Brand Brief и референсы. Не закрывай страницу.</p>}
              {generationError && <div className="mt-4 max-w-xl rounded-xl border border-[#E3D4BE] bg-[#FFFDF8] p-4" role="alert" data-testid="visual-direction-generation-error">
                <p className="text-[13px] leading-relaxed text-[#5B4326]">{generationError}</p>
                <p className="mt-1 text-[12px] text-[#806B52]">Brand Brief и референсы не изменены.</p>
              </div>}
            </div> : <p className="mt-5 text-[12.5px] text-[#806B52]" data-testid="visual-direction-owner-only-note">Первый запуск визуального анализа доступен в закрытом Owner-эксперименте.</p>}
          </section>}
        </>}
        <footer className="pt-10 pb-2">
          <BrandStudioSignature />
        </footer>
      </main>
    </BrandStudioShell>
  );
}
